import { Undo2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Etiqueta } from '@/components/ui/etiqueta';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoPrecio } from '@/lib/utils/formato-precio';
import { cn } from '@/lib/utils';
import { margenDe } from '@/features/pricing-rules/lib/recorrido-del-precio';
import { historialDePreciosApi } from '../services/historial-de-precios-api';
import type {
  CambioDelHistorial,
  ResultadoDeVolverAEstePrecio,
  VueltaRevisada,
} from '../types/historial-de-precios.types';
import {
  cuantoCambia,
  deCuandoEsElPrecio,
  estrategiaDeLaVuelta,
  margenEnTexto,
  opcionesDePrecio,
  porQueNoSeDeshaceEnTexto,
  reglasDeLaVuelta,
  type OpcionDeEstrategia,
  type OpcionDePrecio,
} from '../lib/volver-a-este-precio';
import { EncabezadoDeVentana, PIE_DE_VENTANA } from '@/features/compite/ui/ventana-compite';

/**
 * «¿Volver a X?» (lote C2b, spec 246 punto 11, D15; maqueta `c2-volver`).
 * Antes de volver dice cuánto cambia, cuánto ganás, si cumple las reglas de
 * hoy y qué hará la estrategia. Volver se aplica en el momento y agrega un
 * cambio nuevo: no borra nada. Un solo botón azul, que dice lo que hace.
 *
 * `alPrecioDeAntes` (ronda del 5-oct): deshacer ese cambio, volver al precio
 * que tenía antes. La misma revisión; el servidor frena si después hubo otro.
 * «Sacarlo de sus estrategias» tampoco viene marcado (DO-4).
 */

function Opcion({
  nombre,
  titulo,
  detalle,
  precio,
  elegida,
  onElegir,
}: {
  nombre: string;
  titulo: string;
  detalle: string;
  precio?: number;
  elegida: boolean;
  onElegir: () => void;
}) {
  const idDelDetalle = useId();
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-md border p-3',
        elegida && 'border-primary ring-1 ring-primary'
      )}
    >
      <input
        type="radio"
        name={nombre}
        aria-label={titulo}
        aria-describedby={idDelDetalle}
        checked={elegida}
        onChange={onElegir}
        className="mt-1"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{titulo}</span>
        <span id={idDelDetalle} className="block text-xs text-muted-foreground">
          {detalle}
        </span>
      </span>
      {precio != null && (
        <span className="text-sm font-semibold tabular-nums">{formatoPrecio(precio)}</span>
      )}
    </label>
  );
}

/** 409 o 422: lo que dijo el servidor no cambia reintentando (sí un error de red). */
function esDefinitivo(e: unknown): boolean {
  const status = (e as { response?: { status?: number } } | null)?.response?.status;
  return status === 409 || status === 422;
}

/** M3: sin precio de antes no se arma «¿Volver a ?». */
function tituloDeLaVentana(destino: number | null, alPrecioDeAntes: boolean): string {
  if (destino == null)
    return alPrecioDeAntes ? '¿Volver al precio de antes?' : '¿Volver a ese precio?';
  return alPrecioDeAntes
    ? `¿Volver a ${formatoPrecio(destino)}, el precio de antes?`
    : `¿Volver a ${formatoPrecio(destino)}?`;
}

export function VolverAEstePrecioDialog({
  productId,
  productName,
  cambio,
  alPrecioDeAntes = false,
  onCerrar,
  onListo,
}: {
  productId: string;
  productName: string;
  /**
   * El cambio al que se quiere volver; null = cerrada. Sólo lo que la ventana
   * usa: así Aprobadas la abre con lo que trae su fila (tarea 7).
   */
  cambio:
    | (Pick<CambioDelHistorial, 'cambioId' | 'antes'> & {
        /**
         * El precio que dejó ese cambio. Sólo hace falta para «Volver a X»; al
         * deshacer (`alPrecioDeAntes`) no se usa, y Aprobadas no lo tiene.
         */
        despues?: CambioDelHistorial['despues'];
      })
    | null;
  /** Deshacer: al precio de ANTES de ese cambio, no al que dejó. */
  alPrecioDeAntes?: boolean;
  onCerrar: () => void;
  onListo: (r: ResultadoDeVolverAEstePrecio, usadoElSugerido: boolean) => void;
}) {
  const [revision, setRevision] = useState<VueltaRevisada | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [intento, setIntento] = useState(0);
  const [precioElegido, setPrecioElegido] = useState<OpcionDePrecio>('USAR');
  const [estrategiaElegida, setEstrategiaElegida] = useState<OpcionDeEstrategia>('DEJAR');
  const [motivo, setMotivo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [errorAlVolver, setErrorAlVolver] = useState<string | null>(null);
  // Sólo el 409 (el precio cambió / ya lo tiene) apaga el botón: reabrir la
  // ventana es lo que lo destraba. Un error de red deja reintentar (ronda 2).
  const [errorDeCambio, setErrorDeCambio] = useState(false);
  // A dónde vuelve: al precio que dejó ese cambio, o al de antes (deshacer).
  const destino = cambio ? (alPrecioDeAntes ? cambio.antes : (cambio.despues ?? null)) : null;

  useEffect(() => {
    if (!cambio?.cambioId) return;
    let vivo = true;
    setRevision(null);
    setError(null);
    setErrorAlVolver(null);
    setErrorDeCambio(false);
    setMotivo('');
    setEstrategiaElegida('DEJAR');
    historialDePreciosApi
      .revisarLaVuelta(productId, cambio.cambioId, alPrecioDeAntes)
      .then((r) => {
        if (!vivo) return;
        setRevision(r);
        setPrecioElegido(r.revision.sugerido != null ? 'USAR' : 'IGUAL');
      })
      .catch((e) => vivo && setError(e ?? true));
    return () => {
      vivo = false;
    };
    // A18: el modo también: el mismo cambio en el otro modo es otra revisión.
  }, [productId, cambio?.cambioId, alPrecioDeAntes, intento]);

  const precios = revision ? opcionesDePrecio(revision) : null;
  const precioFinal =
    precios?.find((o) => o.clave === precioElegido)?.precio ??
    revision?.cambio.precio ??
    destino ??
    0;
  // La cabecera habla del precio final: con «Usar Z» elegido, del sugerido.
  const margenDelFinal = revision ? margenDe(precioFinal, revision.costo) : null;
  const estrategia = revision ? estrategiaDeLaVuelta(revision, precioFinal) : null;
  const reglas = revision ? reglasDeLaVuelta(revision) : null;
  const usar = precios != null && precioElegido === 'USAR';
  const boton = `${usar ? 'Usar' : 'Volver a'} ${formatoPrecio(precioFinal)}`;
  // Al deshacer, la revisión ya avisa si no se puede (y por qué): no se intenta.
  const noSeDeshace =
    alPrecioDeAntes &&
    revision != null &&
    (revision.deshacible === false || revision.sinDeshacer != null);
  const apagado = !revision || trabajando || errorDeCambio || noSeDeshace;

  const volver = async () => {
    if (!cambio?.cambioId) return;
    setTrabajando(true);
    setErrorAlVolver(null);
    try {
      const r = await historialDePreciosApi.volver(productId, {
        cambioId: cambio.cambioId,
        usarElQueCumple: usar,
        // Auditoría H1: el precio del botón; si el servidor decide otro, no vuelve (409).
        precioVisto: precioFinal,
        sacarDeEstrategias: estrategia != null && estrategiaElegida === 'SACAR',
        motivo: motivo.trim() || null,
        ...(alPrecioDeAntes ? { alPrecioDeAntes: true } : {}),
      });
      onListo(r, usar);
    } catch (e) {
      setErrorAlVolver(mensajeDeError(e, 'No se pudo volver a ese precio: no cambió nada.'));
      // M3: el 422 (sin precio de antes) tampoco se arregla reintentando.
      setErrorDeCambio(esDefinitivo(e));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open={cambio != null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-w-[560px]">
        <EncabezadoDeVentana icono={Undo2}>
          <DialogTitle>{tituloDeLaVentana(destino, alPrecioDeAntes)}</DialogTitle>
          <DialogDescription>
            {productName}
            {revision ? ` · ${deCuandoEsElPrecio(revision, alPrecioDeAntes)}` : ''}
          </DialogDescription>
        </EncabezadoDeVentana>

        {error ? (
          <EstadoError
            onReintentar={esDefinitivo(error) ? undefined : () => setIntento((n) => n + 1)}
          >
            {mensajeDeError(error, 'No se pudieron revisar tus reglas para ese precio.')}
          </EstadoError>
        ) : !revision || !reglas ? (
          <div role="status" className="space-y-2" aria-label="Revisando ese precio">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-semibold tabular-nums">
                <span className="mr-1 text-sm font-normal text-muted-foreground">
                  Hoy {formatoPrecio(revision.precioHoy)} →
                </span>
                {formatoPrecio(precioFinal)}
              </span>
              {(() => {
                const c = cuantoCambia(revision.precioHoy, precioFinal);
                return <Etiqueta tono={c.sube ? 'ok' : 'baja'}>{c.texto}</Etiqueta>;
              })()}
              {margenEnTexto(margenDelFinal) ? (
                <Etiqueta tono="neutro">{margenEnTexto(margenDelFinal)}</Etiqueta>
              ) : (
                <Etiqueta tono="alerta">sin costo: no se mide el margen</Etiqueta>
              )}
            </div>

            {noSeDeshace && (
              <Aviso tono="bloqueo" titulo="No se puede deshacer">
                {porQueNoSeDeshaceEnTexto(revision.sinDeshacer)}
              </Aviso>
            )}

            <Aviso tono={reglas.tono} titulo={reglas.titulo}>
              {reglas.detalle}
            </Aviso>

            {precios && (
              <div role="radiogroup" aria-label="¿Qué hacés?" className="space-y-2">
                <p className="text-sm font-semibold">¿Qué hacés?</p>
                {precios.map((o) => (
                  <Opcion
                    key={o.clave}
                    nombre="precio-de-la-vuelta"
                    titulo={o.titulo}
                    detalle={o.detalle}
                    precio={o.precio}
                    elegida={precioElegido === o.clave}
                    onElegir={() => setPrecioElegido(o.clave)}
                  />
                ))}
              </div>
            )}

            {estrategia && (
              <div role="radiogroup" aria-label="La estrategia" className="space-y-2">
                <p className="text-sm font-semibold">{estrategia.titulo}</p>
                {estrategia.opciones.map((o) => (
                  <Opcion
                    key={o.clave}
                    nombre="estrategia-de-la-vuelta"
                    titulo={o.titulo}
                    detalle={o.detalle}
                    elegida={estrategiaElegida === o.clave}
                    onElegir={() => setEstrategiaElegida(o.clave)}
                  />
                ))}
              </div>
            )}

            <label className="block space-y-1">
              <span className="text-sm font-medium">
                Motivo <span className="font-normal text-muted-foreground">(opcional)</span>
              </span>
              <Input
                value={motivo}
                maxLength={200}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Por qué vuelve a este precio"
              />
            </label>

            {errorAlVolver && <EstadoError>{errorAlVolver}</EstadoError>}
          </div>
        )}

        <DialogFooter className={cn(PIE_DE_VENTANA, 'sm:justify-between')}>
          <p className="text-xs text-muted-foreground">
            Se agrega como un cambio nuevo: no se borra nada.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button onClick={volver} disabled={apagado}>
              {boton}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
